// Layouts/Assignments/ViewAssignment.tsx
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { useAuth } from '../../stateManagers/services/AuthContext';
import StudentSubmissionUpload from './parts/StudentSubmissionUpload';

/* ─── Design tokens ──────────────────────────────────────────────── */
const C = {
  navy:        '#0e225d',
  navyMid:     '#1a3370',
  navyHover:   'rgba(14,34,93,0.06)',
  gold:        '#fbb21f',
  goldDim:     'rgba(251,178,31,0.18)',
  goldBorder:  'rgba(251,178,31,0.35)',
  white:       '#ffffff',
  border:      '#e5e7eb',
  bg:          '#f5f7fa',
  muted:       '#9ca3af',
  sub:         '#6b7280',
  body:        '#1f2937',
  green:       '#16a34a',
  greenDim:    'rgba(22,163,74,0.12)',
  greenBorder: 'rgba(22,163,74,0.3)',
  red:         '#dc2626',
  redDim:      'rgba(220,38,38,0.08)',
  redBorder:   'rgba(220,38,38,0.25)',
  amber:       '#f59e0b',
  amberDim:    'rgba(245,158,11,0.12)',
  amberBorder: 'rgba(245,158,11,0.35)',
};

/* ─── Types ──────────────────────────────────────────────────────── */
interface AssignmentFile {
  file_name:   string;
  key:         string;
  url:         string;
  size:        number;
  mime_type:   string;
  uploaded_at: string;
}

interface AssignmentData {
  assignment_id:          string;
  institute_id:           string;
  institute_name:         string;
  branch_id:              string;
  branch_name:            string;
  batch_id:               string | null;
  batch_name:             string | null;
  assignment_title:       string;
  assignment_body:        string;
  files:                  AssignmentFile[];
  start_date:             string | null;
  start_time:             string | null;
  submission_date:        string | null;
  submission_time:        string | null;
  allow_late_submissions: boolean;
  notes:                  string;
  created_at:             string;
  created_by:             string;
  created_by_name:        string;
}

export interface SubmissionFile {
  file_name:   string;
  key:         string;
  url:         string;
  size:        number;
  mime_type:   string;
  uploaded_at: string;
}

export interface Submission {
  submission_id:    string;
  assignment_id:    string;
  student_id:       string;
  student_name:     string;
  submission_text:  string;
  files:            SubmissionFile[];
  status:           'draft' | 'submitted' | 'graded';
  submission_date:  string | null;
  is_late:          boolean;
  marks_obtained:   number | null;
  total_marks:      number | null;
  feedback:         string | null;
  reviewed_by:      string | null;
  reviewed_by_name: string | null;
  reviewed_at:      string | null;
  created_at:       string;
  submitted_at:     string | null;
}

/* ─── Helpers ────────────────────────────────────────────────────── */
const formatDate = (d: string | null | undefined) => {
  if (!d) return 'N/A';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatFileSize = (bytes: number) => {
  if (bytes < 1024)        return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getFileIcon = (mimeType: string) => {
  if (mimeType.includes('pdf'))   return 'pi-file-pdf';
  if (mimeType.includes('image')) return 'pi-image';
  if (mimeType.includes('word') || mimeType.includes('doc')) return 'pi-file-word';
  if (mimeType.includes('excel') || mimeType.includes('sheet')) return 'pi-file-excel';
  if (mimeType.includes('zip') || mimeType.includes('rar'))     return 'pi-folder';
  return 'pi-file';
};

/* ─── FileCard ───────────────────────────────────────────────────── */
const FileCard: React.FC<{ file: AssignmentFile }> = ({ file }) => (
  <a
    href={file.url}
    target="_blank"
    rel="noopener noreferrer"
    style={{ textDecoration: 'none' }}
  >
    <div
      className="va-file-card"
      style={{
        display:       'flex',
        alignItems:    'center',
        gap:           '12px',
        padding:       '12px 14px',
        background:    C.white,
        borderRadius:  '12px',
        border:        `1px solid ${C.border}`,
        cursor:        'pointer',
        transition:    'all 0.15s ease',
      }}
    >
      <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: C.navyHover, border: `1px solid rgba(14,34,93,0.12)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <i className={`pi ${getFileIcon(file.mime_type)}`} style={{ fontSize: '1.1rem', color: C.navy }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '0.83rem', fontWeight: '600', color: C.navy, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {file.file_name}
        </div>
        <div style={{ fontSize: '0.73rem', color: C.muted, marginTop: '2px' }}>
          {formatFileSize(file.size)}
        </div>
      </div>
      <i className="pi pi-download" style={{ fontSize: '1rem', color: C.gold, flexShrink: 0 }} />
    </div>
  </a>
);

/* ─── InfoRow ────────────────────────────────────────────────────── */
const InfoRow: React.FC<{ icon: string; iconColor: string; label: string; value: string }> = ({ icon, iconColor, label, value }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', paddingTop: '12px', paddingBottom: '12px' }}>
    <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: C.navyHover, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <i className={`pi ${icon}`} style={{ fontSize: '0.9rem', color: iconColor }} />
    </div>
    <div>
      <div style={{ fontSize: '0.68rem', fontWeight: '700', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '2px' }}>{label}</div>
      <div style={{ fontSize: '0.85rem', fontWeight: '600', color: C.navy }}>{value}</div>
    </div>
  </div>
);

/* ─── SectionHeader ──────────────────────────────────────────────── */
const SectionHeader: React.FC<{ title: string; badge?: string | number }> = ({ title, badge }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
    <div>
      <div style={{ fontSize: '0.9rem', fontWeight: '700', color: C.navy }}>{title}</div>
      <div style={{ width: '36px', height: '3px', background: C.gold, borderRadius: '2px', marginTop: '5px' }} />
    </div>
    {badge !== undefined && (
      <div style={{ background: C.navy, color: C.gold, fontSize: '0.78rem', fontWeight: '700', padding: '4px 12px', borderRadius: '20px', border: `2px solid ${C.gold}` }}>
        {badge}
      </div>
    )}
  </div>
);

/* ─── Main component ─────────────────────────────────────────────── */
const ViewAssignment: React.FC = () => {
  const { id }    = useParams<{ id: string }>();
  const navigate  = useNavigate();
  const toast     = useRef<Toast>(null);
  const { user }  = useAuth();

  const [assignment,    setAssignment]    = useState<AssignmentData | null>(null);
  const [mySubmission,  setMySubmission]  = useState<Submission | null>(null);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState<string | null>(null);
  const [refreshing,    setRefreshing]    = useState(false);

  const isStudent = user?.role === 'student';

  /* ── fetch assignment ── */
  const fetchAssignment = async () => {
    if (!id) { setError('Assignment ID not found'); setLoading(false); return; }
    try {
      setError(null);
      const res = await apiClient.get(`/v1/assignments/${id}`);
      if (res.data.success) {
        setAssignment(res.data.data);
      } else {
        setError('Failed to fetch assignment details');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch assignment details');
    }
  };

  /* ── fetch my submission (students only) ── */
  const fetchMySubmission = async () => {
    if (!isStudent || !id) return;
    try {
      const res = await apiClient.get(`/v1/assignment-submissions/my?assignment_id=${id}`);
      if (res.data.success && res.data.data.length > 0) {
        setMySubmission(res.data.data[0]);
      } else {
        setMySubmission(null);
      }
    } catch {
      // no submission yet — silent
    }
  };

  /* ── initial load ── */
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchAssignment();
      setLoading(false);
    };
    init();
  }, [id]);

  /* ── fetch submission once assignment loaded ── */
  useEffect(() => {
    if (assignment) fetchMySubmission();
  }, [assignment]);

  /* ── refresh ── */
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAssignment();
    await fetchMySubmission();
    setRefreshing(false);
  };

  const handleSubmissionUpdate = () => fetchMySubmission();

  const getSubmissionDeadline = () => {
    if (!assignment?.submission_date) return null;
    return `${assignment.submission_date} ${assignment.submission_time || '23:59:59'}`;
  };

  /* ── loading ── */
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', background: C.white, borderRadius: '16px', border: `1px solid ${C.border}` }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '64px', height: '64px', borderRadius: '32px', background: '#E8EEF7', border: `2px solid ${C.gold}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
            <ProgressSpinner style={{ width: '34px', height: '34px' }} strokeWidth="3" animationDuration=".8s" />
          </div>
          <p style={{ margin: 0, color: C.navy, fontSize: '0.92rem', fontWeight: '600' }}>Loading assignment…</p>
        </div>
      </div>
    );
  }

  /* ── error ── */
  if (error) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', padding: '40px', textAlign: 'center' }}>
        <div style={{ width: '72px', height: '72px', borderRadius: '36px', background: C.redDim, border: `2px solid ${C.gold}`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
          <i className="pi pi-exclamation-circle" style={{ fontSize: '1.8rem', color: C.red }} />
        </div>
        <p style={{ fontSize: '1.1rem', fontWeight: '700', color: C.navy, margin: '0 0 8px 0' }}>Oops!</p>
        <p style={{ fontSize: '0.88rem', color: C.sub, margin: '0 0 20px 0' }}>{error}</p>
        <button
          onClick={() => { setLoading(true); fetchAssignment().then(() => setLoading(false)); }}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '10px 22px', background: C.navy, color: C.white, border: `2px solid ${C.gold}`, borderRadius: '12px', fontSize: '0.88rem', fontWeight: '700', cursor: 'pointer' }}
        >
          <i className="pi pi-refresh" style={{ fontSize: '0.82rem' }} /> Try Again
        </button>
      </div>
    );
  }

  if (!assignment) return null;

  return (
    <>
      <style>{`
        .va-file-card:hover { background: ${C.navyHover} !important; border-color: rgba(14,34,93,0.2) !important; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(14,34,93,0.08); }
        .va-info-divider { height: 1px; background: #f3f4f6; margin: 0; }
        @keyframes va-fadein { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        .va-section { animation: va-fadein 0.22s ease-out both; }
      `}</style>

      <Toast ref={toast} />

      <div style={{ background: C.bg, minHeight: '100%' }}>

        {/* ── Back + Refresh bar ─────────────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
          <button
            onClick={() => navigate('/assignments')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '8px 16px', background: C.white, border: `1px solid ${C.border}`, borderRadius: '10px', fontSize: '0.83rem', fontWeight: '600', color: C.navy, cursor: 'pointer', transition: 'all 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.background = C.navyHover; e.currentTarget.style.borderColor = 'rgba(14,34,93,0.2)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = C.white; e.currentTarget.style.borderColor = C.border; }}
          >
            <i className="pi pi-arrow-left" style={{ fontSize: '0.75rem' }} /> Back to Assignments
          </button>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', background: C.white, border: `1px solid ${C.border}`, borderRadius: '10px', fontSize: '0.82rem', fontWeight: '600', color: C.sub, cursor: refreshing ? 'not-allowed' : 'pointer', opacity: refreshing ? 0.6 : 1, transition: 'all 0.15s' }}
          >
            <i className={`pi pi-refresh ${refreshing ? 'pi-spin' : ''}`} style={{ fontSize: '0.75rem' }} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>

        {/* ── Hero header ───────────────────────────────────────── */}
        <div
          className="va-section"
          style={{
            background:   `linear-gradient(180deg, ${C.navy} 0%, ${C.navyMid} 100%)`,
            borderRadius: '20px',
            padding:      '26px 28px 22px',
            marginBottom: '20px',
            position:     'relative',
            overflow:     'hidden',
            boxShadow:    '0 4px 18px rgba(14,34,93,0.22)',
            animationDelay: '0ms',
          }}
        >
          {/* Glow blob */}
          <div style={{ position: 'absolute', top: '-50px', right: '-50px', width: '200px', height: '200px', background: C.goldDim, borderRadius: '50%', filter: 'blur(50px)', pointerEvents: 'none' }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            {/* Breadcrumb pill */}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '3px 12px', background: C.goldDim, border: `1px solid ${C.goldBorder}`, borderRadius: '20px', fontSize: '0.68rem', fontWeight: '700', color: C.gold, letterSpacing: '0.6px', textTransform: 'uppercase', marginBottom: '12px' }}>
              <i className="pi pi-file-edit" style={{ fontSize: '0.65rem' }} /> Assignment
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h1 style={{ margin: '0 0 10px 0', fontSize: '1.45rem', fontWeight: '800', color: C.white, letterSpacing: '-0.3px', lineHeight: 1.3, wordBreak: 'break-word' }}>
                  {assignment.assignment_title}
                </h1>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {/* Batch badge */}
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', borderRadius: '8px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.18)', fontSize: '0.74rem', fontWeight: '600', color: 'rgba(255,255,255,0.85)' }}>
                    <i className={`pi ${assignment.batch_id ? 'pi-users' : 'pi-globe'}`} style={{ fontSize: '0.65rem' }} />
                    {assignment.batch_name ?? 'All Batches'}
                  </div>
                  {/* Branch badge */}
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', borderRadius: '8px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.18)', fontSize: '0.74rem', fontWeight: '600', color: 'rgba(255,255,255,0.85)' }}>
                    <i className="pi pi-building" style={{ fontSize: '0.65rem' }} />
                    {assignment.branch_name}
                  </div>
                  {/* Late OK badge */}
                  {assignment.allow_late_submissions && (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', borderRadius: '8px', background: C.greenDim, border: `1px solid ${C.greenBorder}`, fontSize: '0.74rem', fontWeight: '700', color: C.green }}>
                      <i className="pi pi-clock" style={{ fontSize: '0.65rem' }} /> Late OK
                    </div>
                  )}
                </div>
              </div>

              {/* Files count stat */}
              <div style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '12px', padding: '12px 18px', textAlign: 'center', backdropFilter: 'blur(10px)', flexShrink: 0 }}>
                <p style={{ margin: 0, fontSize: '1.6rem', fontWeight: '800', color: C.gold, lineHeight: 1 }}>{assignment.files.length}</p>
                <p style={{ margin: '3px 0 0 0', fontSize: '0.65rem', color: 'rgba(255,255,255,0.55)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Files</p>
              </div>
            </div>
          </div>

          {/* Bottom accent */}
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '3px', background: `linear-gradient(90deg, ${C.gold} 0%, transparent 100%)` }} />
        </div>

        {/* ── Two-column layout ──────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '20px', alignItems: 'start' }}>

          {/* ── LEFT column ─────────────────────────────────────── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

            {/* Info card */}
            <div
              className="va-section"
              style={{ background: C.white, borderRadius: '16px', border: `1px solid ${C.border}`, overflow: 'hidden', boxShadow: '0 1px 4px rgba(14,34,93,0.05)', animationDelay: '40ms' }}
            >
              {/* Top accent */}
              <div style={{ height: '3px', background: `linear-gradient(90deg, ${C.navy} 0%, ${C.gold} 100%)` }} />
              <div style={{ padding: '4px 18px' }}>
                <InfoRow icon="pi-calendar"       iconColor={C.navy}  label="Start Date"       value={`${formatDate(assignment.start_date)}${assignment.start_time ? ' · ' + assignment.start_time : ''}`} />
                <div className="va-info-divider" />
                <InfoRow icon="pi-clock"          iconColor={C.gold}  label="Submission Due"   value={`${formatDate(assignment.submission_date)}${assignment.submission_time ? ' · ' + assignment.submission_time : ''}`} />
                <div className="va-info-divider" />
                <InfoRow icon="pi-user"           iconColor={C.navy}  label="Created By"       value={assignment.created_by_name} />
                <div className="va-info-divider" />
                <InfoRow icon="pi-calendar-plus"  iconColor={C.muted} label="Created At"       value={formatDate(assignment.created_at)} />
              </div>
            </div>

            {/* Description */}
            {assignment.assignment_body && (
              <div className="va-section" style={{ background: C.white, borderRadius: '16px', border: `1px solid ${C.border}`, padding: '20px', boxShadow: '0 1px 4px rgba(14,34,93,0.05)', animationDelay: '80ms' }}>
                <SectionHeader title="Description" />
                <p style={{ margin: 0, fontSize: '0.88rem', color: C.body, lineHeight: 1.75 }}>
                  {assignment.assignment_body}
                </p>
              </div>
            )}

            {/* Notes */}
            {assignment.notes && (
              <div className="va-section" style={{ animationDelay: '120ms' }}>
                <SectionHeader title="Notes" />
                <div style={{ display: 'flex', gap: '12px', padding: '14px 16px', background: '#FFF5E6', borderRadius: '14px', border: `1.5px solid ${C.gold}` }}>
                  <div style={{ width: '32px', height: '32px', flexShrink: 0, borderRadius: '8px', background: C.white, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className="pi pi-info-circle" style={{ fontSize: '1rem', color: C.gold }} />
                  </div>
                  <p style={{ margin: 0, fontSize: '0.86rem', color: C.navy, lineHeight: 1.6 }}>{assignment.notes}</p>
                </div>
              </div>
            )}

            {/* Student submission section */}
            {isStudent && (
              <div className="va-section" style={{ animationDelay: '160ms' }}>
                <StudentSubmissionUpload
                  assignmentId={assignment.assignment_id}
                  assignmentTitle={assignment.assignment_title}
                  submissionDeadline={getSubmissionDeadline()}
                  allowLateSubmissions={assignment.allow_late_submissions}
                  existingSubmission={mySubmission}
                  onSubmissionUpdate={handleSubmissionUpdate}
                  toast={toast}
                />
              </div>
            )}

            {/* Teacher / Admin — submissions placeholder */}
            {!isStudent && (
              <div className="va-section" style={{ background: C.white, borderRadius: '16px', border: `1px solid ${C.border}`, padding: '20px', boxShadow: '0 1px 4px rgba(14,34,93,0.05)', animationDelay: '160ms' }}>
                <SectionHeader title="Submissions" badge={0} />
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '36px 20px', textAlign: 'center' }}>
                  <i className="pi pi-check-square" style={{ fontSize: '2rem', color: C.muted, marginBottom: '12px', opacity: 0.5 }} />
                  <p style={{ margin: 0, fontSize: '0.9rem', color: C.muted }}>No submissions yet</p>
                </div>
              </div>
            )}
          </div>

          {/* ── RIGHT column — Attached Files ────────────────────── */}
          <div className="va-section" style={{ animationDelay: '60ms' }}>
            <div style={{ background: C.white, borderRadius: '16px', border: `1px solid ${C.border}`, padding: '20px', boxShadow: '0 1px 4px rgba(14,34,93,0.05)', position: 'sticky', top: '20px' }}>
              <SectionHeader title="Attached Files" badge={assignment.files.length} />

              {assignment.files.length === 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '32px 16px', textAlign: 'center' }}>
                  <i className="pi pi-file" style={{ fontSize: '1.8rem', color: C.muted, marginBottom: '10px', opacity: 0.45 }} />
                  <p style={{ margin: 0, fontSize: '0.86rem', color: C.muted }}>No files attached</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {assignment.files.map((file, i) => (
                    <FileCard key={i} file={file} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewAssignment;